import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { getPostBySlug, getRecentPosts, searchPosts } from '../data/blogPosts';
import { crmAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import remarkGfm from 'remark-gfm';
import { 
  ArrowLeft, 
  Clock, 
  Calendar, 
  User, 
  Twitter, 
  Linkedin, 
  Facebook,
  Link as LinkIcon,
  ChevronRight,
  ArrowRight,
  BookOpen,
  Sparkles,
  Quote,
  Phone,
  Mail,
  MessageSquare,
  Send,
  Check,
  X,
  Play,
  Search
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Demo booking modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });
  const [isSubmittingDemo, setIsSubmittingDemo] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);

  // Handle demo form submission
  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    if (!demoForm.name.trim()) {
      toast({ title: "Name required", description: "Please enter your name", variant: "destructive" });
      return;
    }
    
    if (!demoForm.email.trim() && !demoForm.phone.trim()) {
      toast({ title: "Contact required", description: "Please enter your email or phone number", variant: "destructive" });
      return;
    }
    
    setIsSubmittingDemo(true);
    
    try {
      const demoData = {
        name: demoForm.name.trim(),
        email: demoForm.email.trim() || null,
        phone: demoForm.phone.trim() ? `${demoForm.countryCode}${demoForm.phone.trim()}` : null,
        message: demoForm.message.trim() || `Interested after reading: ${post?.title}`,
        source: `Blog - ${post?.title?.substring(0, 50) || 'Article'}`
      };
      
      await crmAPI.submitDemoRequest('mraja', demoData);
      
      setDemoSuccess(true);
      toast({ title: "Demo Request Sent!", description: "We'll contact you soon." });
      
      setTimeout(() => {
        setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
        setDemoSuccess(false);
        setShowDemoModal(false);
      }, 2000);
      
    } catch (error) {
      console.error('Demo request error:', error);
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmittingDemo(false);
    }
  };

  useEffect(() => {
    const foundPost = getPostBySlug(slug);
    if (foundPost) {
      setPost(foundPost);
      window.scrollTo(0, 0);
    } else {
      navigate('/blog');
    }
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = `https://pipercard.com/blog/${slug}`;
  const shareText = post?.title || '';

  const handleShare = (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const relatedPosts = getRecentPosts(4).filter(p => p.slug !== slug).slice(0, 3);

  // Custom components for ReactMarkdown
  const MarkdownComponents = {
    h2: ({ children }) => (
      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mt-12 mb-6 pb-3 border-b border-gray-100">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl lg:text-2xl font-semibold text-gray-800 mt-10 mb-4">
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p className="text-gray-600 text-lg leading-relaxed mb-6">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="my-6 space-y-3">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-6 space-y-3 list-decimal list-inside">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-gray-600 text-lg leading-relaxed flex items-start gap-3">
        <span className="w-2 h-2 bg-amber-500 rounded-full mt-3 flex-shrink-0"></span>
        <span>{children}</span>
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    a: ({ href, children }) => {
      // Check if this is a CTA link (starts with →)
      const childText = typeof children === 'string' ? children : 
        (Array.isArray(children) ? children.join('') : '');
      const isCTA = childText.includes('→') || childText.includes('Create Your');
      
      if (isCTA) {
        return (
          <Link 
            to={href}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 no-underline"
          >
            {childText.replace('→ ', '')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        );
      }
      
      return (
        <a 
          href={href} 
          className="text-amber-600 font-medium hover:text-amber-700 underline decoration-amber-200 hover:decoration-amber-500 underline-offset-2 transition-colors"
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        >
          {children}
        </a>
      );
    },
    blockquote: ({ children }) => (
      <blockquote className="my-8 relative">
        <div className="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-amber-500 to-orange-500 rounded-full"></div>
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 pl-8 border border-amber-100">
          <Quote className="w-8 h-8 text-amber-400 mb-3" />
          <div className="text-gray-700 text-lg italic leading-relaxed">
            {children}
          </div>
        </div>
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="my-8 overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table className="w-full text-left">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-50 border-b border-gray-200">
        {children}
      </thead>
    ),
    th: ({ children }) => (
      <th className="px-6 py-4 text-sm font-semibold text-gray-900 uppercase tracking-wider">
        {children}
      </th>
    ),
    td: ({ children }) => {
      // Convert Yes/No to icons
      const content = typeof children === 'string' ? children : 
        (children?.props?.children || children);
      const textContent = String(content).trim().toLowerCase();
      
      if (textContent === 'yes') {
        return (
          <td className="px-6 py-4 border-b border-gray-100">
            <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
              <Check className="w-5 h-5 text-green-600" />
            </span>
          </td>
        );
      }
      if (textContent === 'no') {
        return (
          <td className="px-6 py-4 border-b border-gray-100">
            <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
              <X className="w-5 h-5 text-red-500" />
            </span>
          </td>
        );
      }
      if (textContent === 'basic' || textContent === 'limited') {
        return (
          <td className="px-6 py-4 border-b border-gray-100">
            <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
              {content}
            </span>
          </td>
        );
      }
      if (textContent === 'advanced') {
        return (
          <td className="px-6 py-4 border-b border-gray-100">
            <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
              {content}
            </span>
          </td>
        );
      }
      if (textContent === 'event-only') {
        return (
          <td className="px-6 py-4 border-b border-gray-100">
            <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
              {content}
            </span>
          </td>
        );
      }
      
      return (
        <td className="px-6 py-4 text-gray-600 border-b border-gray-100">
          {children}
        </td>
      );
    },
    tr: ({ children }) => (
      <tr className="hover:bg-gray-50 transition-colors">
        {children}
      </tr>
    ),
    hr: () => (
      <hr className="my-12 border-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
    ),
    img: ({ src, alt }) => (
      <figure className="my-8">
        <img 
          src={src} 
          alt={alt} 
          className="w-full rounded-2xl shadow-lg"
        />
        {alt && (
          <figcaption className="text-center text-sm text-gray-500 mt-3 italic">
            {alt}
          </figcaption>
        )}
      </figure>
    ),
    code: ({ children }) => (
      <code className="bg-gray-100 text-amber-600 px-2 py-1 rounded text-sm font-mono">
        {children}
      </code>
    ),
  };

  if (!post) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  // Generate Article Schema for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.metaDescription || post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Organization",
      "name": "Piper Card",
      "url": "https://pipercard.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Piper Card",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pipercard.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://pipercard.com/blog/${post.slug}`
    },
    "keywords": post.keywords?.join(', ') || post.tags?.join(', '),
    "articleSection": post.category,
    "wordCount": post.content?.split(' ').length || 1500,
    "inLanguage": "en-IN"
  };

  // Generate FAQ Schema if content has FAQ section
  const generateFAQSchema = () => {
    if (!post.content?.includes('### FAQ')) return null;
    
    // Extract FAQ section and parse Q&A pairs
    const faqMatch = post.content.match(/### FAQ[:\s]*([\s\S]*?)(?=###|$)/i);
    if (!faqMatch) return null;
    
    const faqContent = faqMatch[1];
    const qaPattern = /\*\*([^*]+)\*\*\s*\n([^*]+?)(?=\*\*|$)/g;
    const faqs = [];
    let match;
    
    while ((match = qaPattern.exec(faqContent)) !== null) {
      faqs.push({
        "@type": "Question",
        "name": match[1].trim(),
        "acceptedAnswer": {
          "@type": "Answer",
          "text": match[2].trim()
        }
      });
    }
    
    if (faqs.length === 0) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs
    };
  };
  
  const faqSchema = generateFAQSchema();

  // Generate BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pipercard.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://pipercard.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://pipercard.com/blog/${post.slug}`
      }
    ]
  };

  return (
    <Layout>
      <SEO 
        title={post.title}
        description={post.metaDescription || post.excerpt}
        keywords={post.keywords?.join(', ') || post.tags?.join(', ')}
        image={post.image}
        type="article"
        publishedTime={post.date}
        modifiedTime={post.date}
        canonicalUrl={`https://pipercard.com/blog/${post.slug}`}
        structuredData={[articleSchema, breadcrumbSchema, faqSchema].filter(Boolean)}
      />

      <article className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative bg-gray-900 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/90 to-gray-900/70" />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 pt-8 pb-16 lg:pt-12 lg:pb-24">
            {/* Search Box */}
            <div className="mb-8 relative">
              <div className="relative max-w-xl">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchResults(e.target.value.length > 0);
                  }}
                  onFocus={() => searchQuery && setShowSearchResults(true)}
                  className="pl-12 pr-4 py-3 bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 rounded-xl text-base focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="blog-post-search"
                />
                {/* Search Results Dropdown */}
                {showSearchResults && searchQuery && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-80 overflow-y-auto z-50">
                    {searchPosts(searchQuery).slice(0, 5).map((result) => (
                      <Link
                        key={result.id}
                        to={`/blog/${result.slug}`}
                        onClick={() => {
                          setShowSearchResults(false);
                          setSearchQuery('');
                        }}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      >
                        <img 
                          src={result.image} 
                          alt={result.title}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{result.title}</p>
                          <p className="text-xs text-gray-500">{result.category}</p>
                        </div>
                      </Link>
                    ))}
                    {searchPosts(searchQuery).length === 0 && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No articles found for "{searchQuery}"
                      </div>
                    )}
                    {searchPosts(searchQuery).length > 0 && (
                      <Link
                        to={`/blog?search=${encodeURIComponent(searchQuery)}`}
                        onClick={() => setShowSearchResults(false)}
                        className="block p-3 text-center text-amber-600 hover:bg-amber-50 text-sm font-medium"
                      >
                        View all results →
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
              <Link to="/" className="hover:text-amber-400 transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/blog" className="hover:text-amber-400 transition-colors">Blog</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-500 truncate max-w-[200px]">{post.category}</span>
            </nav>

            {/* Category */}
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-4 py-1.5 text-sm font-medium mb-6">
              {post.category}
            </Badge>
            
            {/* Title */}
            <h1 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>
            
            {/* Excerpt */}
            <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
              {post.excerpt}
            </p>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-gray-400">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{post.author}</p>
                  <p className="text-xs text-gray-500">Author</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="relative -mt-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-12 gap-8">
              {/* Article Content */}
              <div className="lg:col-span-8">
                <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12">
                  {/* Mobile Share */}
                  <div className="flex items-center gap-3 mb-8 lg:hidden pb-6 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Share:</span>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => handleShare('twitter')}>
                        <Twitter className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => handleShare('linkedin')}>
                        <Linkedin className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-9 w-9 p-0" onClick={copyLink}>
                        <LinkIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Article Body */}
                  <div className="article-content">
                    <ReactMarkdown 
                      components={MarkdownComponents}
                      remarkPlugins={[remarkGfm]}
                    >
                      {post.content}
                    </ReactMarkdown>
                  </div>

                  {/* Tags */}
                  <div className="mt-12 pt-8 border-t border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-5 h-5 text-amber-500" />
                      <span className="font-semibold text-gray-900">Related Topics</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span 
                          key={tag} 
                          className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-amber-50 hover:text-amber-700 transition-colors cursor-pointer"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Share CTA */}
                  <div className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <p className="font-semibold text-gray-900">Found this helpful?</p>
                        <p className="text-sm text-gray-500">Share it with your network</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShare('twitter')}
                          className="rounded-full hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500"
                        >
                          <Twitter className="w-4 h-4 mr-2" />
                          Twitter
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShare('linkedin')}
                          className="rounded-full hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700"
                        >
                          <Linkedin className="w-4 h-4 mr-2" />
                          LinkedIn
                        </Button>
                        <Button
                          variant={copied ? "default" : "outline"}
                          size="sm"
                          onClick={copyLink}
                          className={`rounded-full ${copied ? 'bg-green-500 hover:bg-green-600 text-white' : ''}`}
                        >
                          <LinkIcon className="w-4 h-4 mr-2" />
                          {copied ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-4">
                <div className="sticky top-8 space-y-6">
                  {/* CTA Card */}
                  <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-0 overflow-hidden shadow-xl">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>
                    <CardContent className="p-6 relative">
                      <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-4">
                        <Sparkles className="w-4 h-4" />
                        Get Started Free
                      </div>
                      <h3 className="font-bold text-xl mb-2">Create Your Digital Card</h3>
                      <p className="text-gray-400 text-sm mb-6">
                        Join thousands of professionals using Piper Card
                      </p>
                      <Button 
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                        onClick={() => navigate('/card-builder')}
                      >
                        Create Your Card
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Quick Links */}
                  <Card className="border-gray-200 shadow-lg">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-gray-900 mb-4">Explore More</h3>
                      <div className="space-y-1">
                        {[
                          { label: 'Create Digital Card', path: '/card-builder' },
                          { label: 'QR Code Generator', path: '/qr-generator' },
                          { label: 'How NFC Works', path: '/how-nfc-works' },
                          { label: 'All Articles', path: '/blog' },
                        ].map((link) => (
                          <Link 
                            key={link.path}
                            to={link.path} 
                            className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                          >
                            <span className="text-gray-700 group-hover:text-amber-600 transition-colors">{link.label}</span>
                            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Desktop Share */}
                  <Card className="border-gray-200 shadow-lg hidden lg:block">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-gray-900 mb-4">Share Article</h3>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShare('twitter')}
                          className="flex-1 rounded-full"
                        >
                          <Twitter className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShare('linkedin')}
                          className="flex-1 rounded-full"
                        >
                          <Linkedin className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleShare('facebook')}
                          className="flex-1 rounded-full"
                        >
                          <Facebook className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyLink}
                          className="flex-1 rounded-full"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        <div className="py-16 lg:py-20 mt-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">Continue Reading</h2>
              <p className="text-gray-600">More articles you might enjoy</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Card 
                  key={relatedPost.id}
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group border-0 bg-white rounded-2xl shadow-lg"
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                >
                  <div className="aspect-[16/10] relative overflow-hidden">
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-5">
                    <Badge variant="outline" className="mb-3 text-xs rounded-full">{relatedPost.category}</Badge>
                    <h3 className="font-bold text-gray-900 group-hover:text-amber-600 transition-colors line-clamp-2 mb-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-500">{relatedPost.readTime}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl lg:text-4xl font-bold mb-4">
              Ready to Create Your Digital Business Card?
            </h2>
            <p className="text-gray-400 mb-8 text-lg">
              Join thousands of professionals networking smarter with Piper Card
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-amber-500 hover:bg-amber-600 rounded-full px-8 py-6 text-lg"
                onClick={() => navigate('/card-builder')}
              >
                Create Your Free Card
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-gray-600 text-white hover:bg-gray-800 rounded-full px-8 py-6 text-lg"
                onClick={() => navigate('/shop')}
              >
                Browse NFC Cards
              </Button>
            </div>
          </div>
        </div>
      </article>

      {/* Demo Booking Modal */}
      <Dialog open={showDemoModal} onOpenChange={(open) => {
        if (!isSubmittingDemo) {
          setShowDemoModal(open);
          if (!open) {
            setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
            setDemoSuccess(false);
          }
        }
      }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-5 text-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold">Book Your Live Demo</h2>
                  <p className="text-sm text-white/80">Get a personalized walkthrough</p>
                </div>
              </div>
              <button 
                onClick={() => !isSubmittingDemo && setShowDemoModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {demoSuccess ? (
            <div className="px-5 py-10 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Request Sent!</h3>
              <p className="text-gray-600">We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleDemoSubmit} className="px-5 py-5 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="blog-demo-name" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-violet-500" />
                  Your Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="blog-demo-name"
                  placeholder="Enter your full name"
                  value={demoForm.name}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-12 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blog-demo-email" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-violet-500" />
                  Email
                </Label>
                <Input
                  id="blog-demo-email"
                  type="email"
                  placeholder="your@email.com"
                  value={demoForm.email}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full h-12 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blog-demo-phone" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-violet-500" />
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <CountryCodeSelect
                    value={demoForm.countryCode}
                    onChange={(e) => setDemoForm(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="w-24 h-12 text-sm rounded-xl"
                  />
                  <Input
                    id="blog-demo-phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm(prev => ({ ...prev, phone: e.target.value.replace(/[^\d]/g, '') }))}
                    className="flex-1 h-12 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="blog-demo-message" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-violet-500" />
                  Message <span className="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <textarea
                  id="blog-demo-message"
                  placeholder="Tell us about your requirements..."
                  value={demoForm.message}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmittingDemo}
                className="w-full h-12 text-base bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25"
              >
                {isSubmittingDemo ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Book Live Demo
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-400 pt-2">
                Your information will be used to contact you about our services.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default BlogPost;
